import TomSelect from "tom-select";
import type { TomSettings } from "tom-select/dist/esm/types/settings.js";

declare var window: any;

const Select = {
  isUpdating: false,
  remoteSettings(
    remoteUrl: string,
    queryField: string,
    paginationType: string,
  ): any {
    return {
      maxOptions: null,
      firstUrl: function (query: any) {
        const queryParams = paginationType === "cursor" ? "&first=50" : "";
        return `${remoteUrl}?${queryField}=${encodeURIComponent(query)}${queryParams}`;
      },
      load: function (query: any, callback: any) {
        const url = this.getUrl(query);

        fetch(url)
          .then((response) => response.json())
          .then((json) => {
            /*
            Monkeypatch `scrollToOption` to not do anything until `callback` is called
            to prevent scrolling to top when next URL loads.
            Reference issue: https://github.com/orchidjs/tom-select/issues/556
            */
            const _scrollToOption = this.scrollToOption;
            this.scrollToOption = () => {};
            const term = encodeURIComponent(query);
            let nextUrl;

            if (json["has_next_page?"] && paginationType === "cursor") {
              const endCursor = json.end_cursor;
              nextUrl = `${remoteUrl}?${queryField}=${term}&first=50&after=${endCursor}`;
            }

            if (json["has_next_page?"] && paginationType === "page") {
              const nextPage = json.page_number + 1;
              nextUrl = `${remoteUrl}?${queryField}=${term}&page=${nextPage}`;
            }

            this.setNextUrl(query, nextUrl);
            callback(json.entries);
            this.scrollToOption = _scrollToOption;
          })
          .catch(() => {
            callback();
          });
      },
      shouldLoad: function (query: any) {
        return query.length > 1;
      },
    };
  },
  createSettings(this: any, dataset: any): Partial<TomSettings> {
    const {
      labelField,
      valueField,
      options,
      dropdownParent,
      maxOptions,
      optgroups,
      remoteUrl,
      queryField,
      paginationType,
    } = dataset;
    // HTML escape option label only when htmlEscape key exists in dataset since it's a boolean value
    const htmlEscape = dataset.hasOwnProperty("htmlEscape");
    // Selection is clearable by default (× remove button + empty option).
    // `clearable={false}` on the component omits `data-clearable`, which locks
    // the selection: no remove button and the empty option can't be picked.
    const clearable = dataset.hasOwnProperty("clearable");
    const plugins = ["dropdown_input", "no_backspace_delete"];
    if (clearable) plugins.push("remove_button");
    if (remoteUrl) plugins.push("virtual_scroll");

    // For custom select button toggle
    // Prevent default 'mousedown' event from closing the dropdown when clicking the custom button
    // https://github.com/orchidjs/tom-select/blob/7c5f700c46856f66eed00b38f2d669e591fa4c0b/src/tom-select.ts#L381C3-L381C49
    let customSelectButton;
    if (dataset.customSelectButton) {
      customSelectButton = document.querySelector(dataset.customSelectButton);
      if (customSelectButton) {
        customSelectButton.addEventListener("mousedown", (e: Event) => {
          e.stopPropagation();
          e.preventDefault();
        });
      }
    }

    // Use String value of closeAfterSelect in dataset since it can be undefined for default behavior
    // https://tom-select.js.org/docs/#closeafterselect
    let closeAfterSelect;
    if (dataset.closeAfterSelect === "true") closeAfterSelect = true;
    else if (dataset.closeAfterSelect === "false") closeAfterSelect = false;

    const settings: any = {
      labelField: labelField || "label",
      valueField: valueField || "value",
      searchField: [labelField || "label"],
      allowEmptyOption: clearable,
      plugins: plugins,
      options: JSON.parse(options),
      dropdownParent: dropdownParent,
      maxOptions: maxOptions || null,
      optgroups: optgroups ? JSON.parse(optgroups) : [],
      loadingClass: "ts-loading",
      closeAfterSelect: closeAfterSelect,
      render: {
        option: function (data: any, escape: any) {
          const html = htmlEscape
            ? escape(data[settings.labelField])
            : data[settings.labelField];

          if (data.hide) return "<div style='display: none;'></div>";
          return "<div>" + html + "</div>";
        },
        item: function (data: any, escape: any) {
          const html = htmlEscape
            ? escape(data[settings.labelField])
            : data[settings.labelField];
          return "<div>" + html + "</div>";
        },
      },
      onInitialize: function () {
        // Stop escape key from propagating to prevent closing modal
        this.control.addEventListener("keyup", (event: any) => {
          if (event.key == "Escape") event.stopPropagation();
        });

        // If remoteUrl is set, set the placeholder for the search input to indicate that
        // user should type to return results
        if (remoteUrl) {
          const searchInput = this.control_input; // the actual search input element
          searchInput.placeholder = "Type to search...";
        }

        // Stop input event from propagating to prevent triggering form's `phx-change`
        this.control_input.addEventListener("input", (event: any) => {
          event.stopPropagation();
        });
        this.control_input.addEventListener("change", (event: any) => {
          event.stopPropagation();
        });
      },
    };

    if (remoteUrl) {
      const remoteSettings = {
        ...settings,
        ...this.remoteSettings(
          remoteUrl,
          queryField,
          paginationType || "cursor",
        ),
      };
      // Delete options so it will use the default derived from select input
      delete remoteSettings.options;

      return remoteSettings;
    }
    return settings;
  },
  createTomSelect(this: any) {
    // Remove readonly attribute to make it editable by TomSelect
    if (this.el.readOnly) {
      this.el.readOnly = false;
    }

    new TomSelect(this.el, this.createSettings(this.el.dataset));
  },
  mounted(this: any) {
    if (this.el.tomselect) return;
    this.createTomSelect();

    // For manual handling of dropdown open/close with customSelectButton
    this.el.addEventListener("toggleDropdown", () => {
      if (this.el.tomselect.isOpen) this.el.tomselect.close();
      else this.el.tomselect.open();
    });

    this.el.addEventListener("removeItem", (e: any) => {
      this.el.tomselect.removeItem(e.detail.value);
    });
  },
  getSelectValues(select: any) {
    return [...select.options]
      .filter((opt: any) => opt.selected)
      .map((opt: any) => opt.value || opt.text);
  },
  beforeUpdate(this: any) {
    // Store tomselect open state if closeAfterSelect is false
    // To be able to restore open state after update
    if (this.el.tomselect.settings.closeAfterSelect === false)
      this.isOpen = this.el.tomselect.isOpen;
  },
  updated(this: any) {
    // Guard against re-entrant updates that could cause infinite loops
    if (this.isUpdating) return;
    this.isUpdating = true;

    try {
      // Sync tomselect in case select changes value
      // this.el.tomselect.sync();
      // Get tomselect value after sync
      // const val = this.el.tomselect.getValue();
      const values = this.getSelectValues(this.el);
      // Destroy tomselect
      this.el.tomselect.destroy();
      // Create new tomselect instance
      this.createTomSelect();
      // Set options to whatever is set in `data-options` attribute
      // This is useful for manually setting options when options are from remote source
      this.el.tomselect.addOptions(
        JSON.parse(this.el.tomselect.input.dataset.options),
      );
      // Set the value from the destroyed tomselect in silent mode so
      // it will not fire change event
      this.el.tomselect.setValue(values, true);

      // Open after update if it was open before and closeAfterSelect is false
      if (
        this.isOpen &&
        this.el.tomselect.settings.closeAfterSelect === false
      ) {
        this.el.tomselect.open();
      }
    } finally {
      this.isUpdating = false;
    }
  },
};

export default Select;
